/**
 * Последняя позиция, о которой знает ЭТА вкладка.
 *
 * Карточка спрашивает историю у сервера, и обычно этого достаточно. Но уход из
 * плеера и запрос истории — две гонки подряд, и обе карточка проигрывает:
 * позиция уезжает на сервер отдельным запросом, а размонтирование плеера
 * (кнопка «назад» браузера, закрытая вкладка) вообще не умеет ничего ждать —
 * из функции очистки эффекта промис не проводить. В итоге зритель возвращался
 * к прежней полосе: досмотренная до середины серия выглядела нетронутой.
 *
 * Поэтому позиция кладётся ещё и сюда — в тот же миг, когда уходит на сервер.
 * Карточка накладывает её поверх серверного ответа, если наша свежее. Ответ
 * сервера при этом остаётся главным: наша запись живёт минуты и нужна ровно на
 * те секунды, пока история о ней не знает.
 *
 * `sessionStorage`, как и точка возврата (player-return.ts): это про открытую
 * вкладку, а не про зрителя. Настоящее место прогресса — сервер.
 */
const KEY = 'lumos:last-progress';

/**
 * Сколько наша запись считается свежее серверной.
 *
 * Дольше — и мы начали бы спорить с сервером о давнем просмотре, у которого своя
 * правда (другое устройство, сброс прогресса, отметка «просмотрено»). Минуты
 * хватает: за это время либо запрос дошёл, либо не дойдёт уже никогда.
 */
const TTL_MS = 5 * 60_000;

export interface LocalProgress {
  contentId: string;
  season: number | null;
  episode: number | null;
  positionMs: number;
  durationMs: number | null;
  percent: number;
  completed: boolean;
  /** Когда позицию записали здесь: по нему и решается, чья свежее. */
  at: number;
}

/** Запомнить позицию, которую только что отправили серверу. */
export function rememberLocalProgress(entry: Omit<LocalProgress, 'at'>): void {
  try {
    const value: LocalProgress = { ...entry, at: Date.now() };
    window.sessionStorage.setItem(KEY, JSON.stringify(value));
  } catch {
    // Приватный режим без хранилища: полоса обновится после ответа сервера.
  }
}

/** Позиция этой вкладки по тайтлу; `null` — её нет или она уже несвежая. */
export function readLocalProgress(contentId: string): LocalProgress | null {
  try {
    const raw = window.sessionStorage.getItem(KEY);
    if (raw === null) return null;
    const value = JSON.parse(raw) as LocalProgress;
    if (value.contentId !== contentId) return null;
    if (Date.now() - value.at > TTL_MS) return null;
    return value;
  } catch {
    return null;
  }
}

/**
 * Забыть позицию: сменился зритель.
 *
 * Запись здесь принадлежит ПРОСМОТРУ, а не вкладке, и на карточку она
 * накладывается поверх серверного ответа. Пережив выбор другого профиля, она
 * дорисовывала бы новому зрителю чужую полосу — то есть показывала бы ему
 * просмотр, которого у него не было.
 */
export function forgetLocalProgress(): void {
  try {
    window.sessionStorage.removeItem(KEY);
  } catch {
    // Без хранилища забывать нечего.
  }
}
